import React, { useContext } from 'react';
import dynamic from 'next/dynamic';
import { Spinner } from '@/components/Atoms/Spinner/Spinner';
import { QbSurveyInfo } from 'types/qbSurveyInfo';
import { PageContext } from 'src/context/PageContext';
import { SurveyPendingOpen } from 'screens/survey/SurveyErrorScreens/SurveyPendingOpen';

const DynamicCollaborativeMap = dynamic(() => import('./CollaborativeMapWrapper'), {
  loading: () => <Spinner center />,
  ssr: false,
});

interface Props {
  qbSurvey: QbSurveyInfo;
  activityId: string;
}

/**
 * A collaborative map outlives the consultation that filled it.
 *
 * A closed map still renders: its pins are a public record, and residents and officers go back to
 * read them long after contributions stop. Closing only takes away the ability to add to it, which
 * the map already handles — `CollaborativeMap` passes `readOnly` to the canvas, which keeps pins
 * visible while disabling every interaction, and withholds the pin placed, edited and deleted
 * handlers so nothing can be written even if an interaction slipped through. The intro announces
 * the closure in place of the closing date.
 *
 * A map that has not opened yet is different: there is nothing to look at, so it keeps its notice.
 */
export default function CollaborativeMapDynamic({ qbSurvey, activityId }: Props) {
  const { urlParams } = useContext(PageContext);
  if (!urlParams?.preview && qbSurvey?.start_time && new Date() < new Date(qbSurvey.start_time)) {
    return <SurveyPendingOpen />;
  }
  return (
    <div translate="no">
      <DynamicCollaborativeMap qbSurvey={qbSurvey} activityId={activityId} />
    </div>
  );
}
